import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import headlineIcon from "../../assets/OtherTools/headline.png";
import "./LinkedInHeadlineGenerator.css";
import headline1Img from "../../assets/OtherTools/Headline1.png";
import headline2 from "../../assets/OtherTools/Headline2.png";
import headline3 from "../../assets/OtherTools/Headline3.png";
import headline4 from "../../assets/OtherTools/Headline4.png";
import headline5 from "../../assets/OtherTools/Headline5.png";
import { Copy } from "lucide-react";
import Footer from "../../components/Footer/Footer";
import ReadyToStartBox from "../../components/ReadyToStartBox/ReadyToStartBox";
import Navbar from "../../components/Navbar/Navbar";
import DashboardSidebar from "../../components/DashboardSidebar/DashboardSidebar";
import useAuthStore from "../../stores/authStore";

const API_URL = process.env.REACT_APP_API_URL;

export default function LinkedInHeadlineGenerator() {
  const { user } = useAuthStore();
  const location = useLocation();

  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [headlines, setHeadlines] = useState([]);

  const isDashboardLayout = location.pathname.startsWith("/dashboard");

  const handleGenerate = async (e) => {
  e.preventDefault();
  if (!userInput.trim()) return;

  setLoading(true);
  setError(null);
  setHeadlines([]);

  let attempt = 0;
  let success = false;

  while (attempt < 2 && !success) {
    try {
      const res = await fetch(`${API_URL}/api/public/headline-gen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description: userInput }),
         credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to generate headline");

      const data = await res.json();
      if (data?.headlines?.length) {
        setHeadlines(data.headlines.map((h) => h.headline));
        success = true;
      } else {
        throw new Error("No headlines generated");
      }
    } catch (err) {
      attempt++;
      if (attempt >= 2) {
        console.error(err);
        setError("Servers are busy, please try again later.");
      }
    }
  }

  setLoading(false);
};


  return (
    <>
      {isDashboardLayout ? (
        <DashboardSidebar
          userName={user?.name}
          profilePic={user?.profilePic}
        />
      ) : (
        <Navbar />
      )}

      <Helmet>
        <title>Generate Crisp LinkedIn Headlines | Brandly</title>
        <meta
          name="description"
          content="Generate eye-catching LinkedIn headlines instantly. Tell us about your field and achievements, and Brandly will create the perfect professional headline for you."
        />
        <meta
          name="keywords"
          content="LinkedIn headline generator, LinkedIn profile headline, professional headline tool, LinkedIn tips"
        />
        <meta
          property="og:title"
          content="Generate Crisp LinkedIn Headlines"
        />
        <meta
          property="og:description"
          content="Create scroll-stopping LinkedIn headlines that boost your profile visibility. Powered by AI and tailored to your profession."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://yourwebsite.com/linkedin-headline-generator"
        />
        <meta
          property="og:image"
          content="https://yourwebsite.com/og-image-linkedin-headline.jpg"
        />
        <link
          rel="canonical"
          href="https://yourwebsite.com/linkedin-headline-generator"
        />
      </Helmet>

      <header className="lh-hero">
        <div className="lh-icon">
          <img
            src={headlineIcon}
            alt="Headline icon"
            className="lh-icon-img"
          />
        </div>
        <h1>
          Generate <span className="lh-highlight">Crisp</span> LinkedIn Headlines
        </h1>
        <p className="lh-subtitle">
          Tell us about your field and interests and Brandly will generate the perfect headline for you
        </p>
        <form
          className="lh-form"
          aria-label="LinkedIn Headline Generator"
          onSubmit={handleGenerate}
        >
          <input
            type="text"
            id="linkedin-headline-input"
            name="linkedin-headline-input"
            placeholder="Tell us about you and your achievements"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            required
          />
          <button type="submit" className="lh-generate-btn" disabled={loading}>
            {loading ? "Generating..." : "Generate"}
          </button>
        </form>
      </header>

     {headlines.length > 0 && (
  <section className="lh-result">
    <ul className="lh-headline-list">
      {headlines.map((h, i) => (
        <li key={i} className="lh-headline-item">
          <span>“{h}”</span>
          <button
            className="lh-copy-btn"
            onClick={() => navigator.clipboard.writeText(h)}
            aria-label="Copy headline"
          >
            <Copy size={18} strokeWidth={1.8} />
          </button>
        </li>
      ))}
    </ul>

    {/* Regenerate Button */}
    <div className="lh-regenerate-container">
      <button
        className="lh-regenerate-btn"
        onClick={(e) => handleGenerate(e)}
        disabled={loading}
      >
        {loading ? "Regenerating..." : "Regenerate Headlines"}
      </button>
    </div>
  </section>
)}


      {error && <p className="lh-error">{error}</p>}

      <div className="lh-divider"></div>

      {/* Headlines that actually work section */}
      <section className="lhs-section">
        <div className="lhs-container">
          <h2 className="lhs-title">
            5 Creative LinkedIn Headline Styles <br />
            That <span className="lhs-highlight">Actually Work</span>
          </h2>

          <p className="lhs-intro">
            A standout LinkedIn headline can make people pause, click, and connect.
            Whether you’re a recruiter, creator, or job-seeker, your headline tells
            the world what you’re about — often before they read anything else.
          </p>

          <p className="lhs-intro">
            Here are 5 real-world styles that break away from the boring,
            overused buzzwords — and show how your headline can build trust,
            spark curiosity, or just bring a smile.
          </p>

          {/* Headline 1 */}
          <div className="lhs-style">
            <h3 className="lhs-style-title">
              1. Make It About the Candidate, Not Yourself
            </h3>
            <div className="lhs-image-wrapper">
              <img
                src={headline1Img}
                alt="LinkedIn headline example - Make it about the candidate"
                className="lhs-image"
              />
            </div>
            <p className="lhs-description">
              Instead of centering her job title, Ericka Fisher uses her headline
              to spotlight the people she helps. By saying she's{" "}
              <em>"empowering people to change their lives,"</em> she flips the
              script — and makes you want to learn more. It’s warm, people-first,
              and magnetic.
            </p>
          </div>

          {/* Headline 2 */}
          <div className="lhs-style">
            <h3 className="lhs-style-title">
              2. Blend Brand and Role Creatively
            </h3>
            <div className="lhs-image-wrapper">
              <img
                src={headline2}
                alt="LinkedIn headline example - Blend brand and role creatively"
                className="lhs-image"
              />
            </div>
            <p className="lhs-description">
              Erin L’Homme blends her headline with the brand story of Disney by using
              aspirational language — “Imagine the possibilities.” It’s vibrant, fun,
              and instantly recognizable, while her profile photo reinforces the brand.
            </p>
          </div>

          {/* Headline 3 */}
          <div className="lhs-style">
            <h3 className="lhs-style-title">
              3. Trade Buzzwords for Unexpected Language
            </h3>
            <div className="lhs-image-wrapper">
              <img
                src={headline3}
                alt="LinkedIn headline example - Trade buzzwords for unexpected language"
                className="lhs-image"
              />
            </div>
            <p className="lhs-description">
              Alexandra Saxonova avoids the usual corporate clichés and instead uses
              intriguing, approachable wording that makes her role sound human and
              relatable — a refreshing departure from jargon-heavy headlines.
            </p>
          </div>

          {/* Headline 4 */}
          <div className="lhs-style">
            <h3 className="lhs-style-title">
              4. Add Personality — Even with a Simple Emoji
            </h3>
            <div className="lhs-image-wrapper">
              <img
                src={headline4}
                alt="LinkedIn headline example - Add personality with emoji"
                className="lhs-image"
              />
            </div>
            <p className="lhs-description">
              Rajat Sharma shows that a small emoji can pack personality. His playful
              touch makes the headline approachable and memorable — standing out while
              still remaining professional.
            </p>
          </div>

          {/* Headline 5 */}
          <div className="lhs-style">
            <h3 className="lhs-style-title">
              5. Be Bold, Even Funny
            </h3>
            <div className="lhs-image-wrapper">
              <img
                src={headline5}
                alt="LinkedIn headline example - Be bold and funny"
                className="lhs-image"
              />
            </div>
            <p className="lhs-description">
              Jason Yuan uses humor to stand out — “I don’t usually stalk profiles,
              but when I do…” This kind of wit conveys confidence and memorability,
              instantly making his profile more approachable and likable.
            </p>
          </div>
        </div>
      </section>

      <section className="ih-section">
        <div className="ih-container">
          <h2 className="ih-title">
            <span className="ih-highlight">Importance</span> of LinkedIn Headlines
          </h2>
          <p className="ih-text">
            Your LinkedIn headline is the first thing people notice — whether it's your profile or your latest post. It’s your digital first impression, and it needs to be clear, relevant, and scroll-stopping.
          </p>
          <p className="ih-text">
            But writing great headlines is hard. That’s why Brand.ly makes it effortless.
          </p>
          <div className="ih-grid">
            <div className="ih-card">
              <h3 className="ih-card-title">Cut Through the Noise</h3>
              <p className="ih-card-text">
                In a sea of generic profiles and dull posts, your headline is your hook. A strong, tailored headline instantly tells people who you are and why they should care.
              </p>
            </div>
            <div className="ih-card">
              <h3 className="ih-card-title">Boost Visibility & Click-Throughs</h3>
              <p className="ih-card-text">
                Just like a good email subject line, your LinkedIn headline can make or break your visibility. Headlines with clarity and personality get more profile views, more clicks, and more engagement.
              </p>
            </div>
            <div className="ih-card">
              <h3 className="ih-card-title">Made for Speed, Built for Results</h3>
              <p className="ih-card-text">
                With just 2 simple inputs, Brand.ly creates headlines that are optimized for your role, your audience, and your tone — in seconds.
              </p>
            </div>
            <div className="ih-card">
              <h3 className="ih-card-title">Perfect for Personal Branding</h3>
              <p className="ih-card-text">
                Whether you're a founder, marketer, freelancer, or job seeker, your headline is your brand. Brand.ly helps you write one that actually works.
              </p>
            </div>
          </div>
        </div>
      </section>


      {!isDashboardLayout && <ReadyToStartBox />}
      <Footer />
    </>
  );
}
